import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { formatClock } from "./lib/restTimer";

// D11: RP's readiness cues, paraphrased (never their words).
const READY_CUE = "Go again when your breathing has settled and the burn has faded.";

// The bar appears under the finger that just logged a set: the second click
// of that double-click must not skip or extend the rest it started. The
// keyboard (detail 0) and a single click still act.
const once = (act) => (event) => {
  if (event.detail > 1) return;
  act();
};
const holdFocus = (event) => {
  if (event.detail > 1) event.preventDefault();
};

/**
 * The rest timer (D11), pinned to the bottom of the screen while a rest
 * runs — and, once it's over, until it's put away or the next set is
 * logged. The time left is read from the rest's stored end time (see
 * lib/useRestTimer.js), so it is right after a locked screen or a reload.
 *
 * The digits change every second, so they are not a live region: a
 * separate, always-mounted polite status says only that a rest started,
 * that 30 seconds were added, and that it's over.
 */
function RestTimerBar({
  rest,
  remaining,
  total,
  running,
  ended,
  label,
  wakeLockSupported = true,
  onExtend,
  onSkip,
}) {
  const [said, setSaid] = useState("");
  const startedAt = rest ? rest.endsAt - rest.seconds * 1000 : null;
  const seconds = rest?.seconds ?? null;
  // What the last render saw, so each change is said once (a resumed rest
  // after a reload isn't "started" again).
  const seen = useRef({ startedAt, seconds, running });

  useEffect(() => {
    const before = seen.current;
    seen.current = { startedAt, seconds, running };
    if (startedAt === null) return;
    if (startedAt !== before.startedAt && running) {
      setSaid(`Rest ${formatClock(total)} started${label ? ` after ${label}` : ""}.`);
    } else if (seconds !== before.seconds && running) {
      setSaid(`Rest extended: ${formatClock(remaining)} left.`);
    } else if (before.running && !running && ended) {
      setSaid("Rest over — time for the next set.");
    }
  }, [startedAt, seconds, running, ended, total, remaining, label]);

  const share = total > 0 ? Math.min(1, Math.max(0, remaining / total)) : 0;
  return (
    <>
      <span role="status" aria-live="polite" className="sr-only">
        {said}
      </span>
      {rest && (
        <section
          aria-label="Rest timer"
          // Marks a fixed bottom bar: the page's scroll padding keeps a
          // focused control from landing under it (tailwind.css, WCAG 2.4.11).
          data-bottom-bar=""
          className="fixed inset-x-0 bottom-0 z-40 border-t bg-card pb-[env(safe-area-inset-bottom)] shadow-lg"
        >
          <div className="mx-auto flex max-w-2xl flex-col gap-2 px-3 py-3">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="m-0 truncate text-xs text-muted-foreground">
                  {running ? `Rest${label ? ` after ${label}` : ""}` : "Rest over"}
                </p>
                <p
                  className="m-0 font-display text-3xl leading-tight font-semibold tabular-nums"
                  aria-hidden="true"
                >
                  {formatClock(remaining)}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-11 md:h-9"
                onMouseDown={holdFocus}
                onClick={once(() => onExtend())}
              >
                <span aria-hidden="true">+30 s</span>
                <span className="sr-only">Add 30 seconds</span>
              </Button>
              <Button
                type="button"
                variant={running ? "ghost" : "default"}
                className="h-11 md:h-9"
                onMouseDown={holdFocus}
                onClick={once(() => onSkip())}
              >
                {running ? "Skip rest" : "Done"}
              </Button>
            </div>
            <div
              role="progressbar"
              aria-label="Rest left"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={remaining}
              aria-valuetext={`${formatClock(remaining)} left`}
              className="h-1.5 overflow-hidden rounded-full bg-muted"
            >
              <div className="h-full bg-primary transition-[width]" style={{ width: `${share * 100}%` }} />
            </div>
            <p className="m-0 text-xs text-muted-foreground">
              {running ? READY_CUE : "Log the next set when you're ready."}
              {running && !wakeLockSupported
                ? " This browser can't keep the screen on — the timer keeps time anyway."
                : ""}
            </p>
          </div>
        </section>
      )}
    </>
  );
}

RestTimerBar.propTypes = {
  /** The stored rest ({endsAt, seconds, …}), or null when there's none. */
  rest: PropTypes.shape({ endsAt: PropTypes.number, seconds: PropTypes.number }),
  /** Whole seconds left. */
  remaining: PropTypes.number.isRequired,
  /** The rest's length in seconds (with any +30 s). */
  total: PropTypes.number.isRequired,
  running: PropTypes.bool.isRequired,
  ended: PropTypes.bool.isRequired,
  /** The exercise it follows ("Bench press"). */
  label: PropTypes.string,
  /** False: say the screen may sleep. */
  wakeLockSupported: PropTypes.bool,
  onExtend: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

export default RestTimerBar;
